import React, { FC, useCallback, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useRegisterMutation, validateInviteCode } from "@api/userRegister.api";
import { UserSignupPurpose } from "@common/types/user";
import { finishPasswordlessLogin } from "@lib/auth0";
import { Loader } from "@design-system/Loader/Loader";
import { CodeVerificationProps } from "@components/auth/CodeVerification/CodeVerification";
import { login } from "@api/auth.api";
import { useManageSteps } from "../useManageSteps";
import { Register } from "../steps/Register/Register";
import { RegisterCodeVerification } from "../steps/RegisterCodeVerification";
import { OnboardingLayout } from "../layout/OnboardingLayout";

type RegistrationSteps = "start" | "verify";

interface RouteParams {
    inviteCode?: string;
}

function getSignupPurposeFromParam(signupPurpose: string | null) {
    switch (signupPurpose) {
        case UserSignupPurpose.ACCOUNTANT:
            return UserSignupPurpose.ACCOUNTANT;
        case UserSignupPurpose.BUSINESS_OWNER:
            return UserSignupPurpose.BUSINESS_OWNER;
        default:
            return undefined;
    }
}

export const RegistrationFlow: FC = () => {
    const { inviteCode } = useParams() as Readonly<RouteParams>;
    const [searchParams] = useSearchParams();
    const signupPurpose = getSignupPurposeFromParam(
        searchParams.get("signupPurpose"),
    );
    const [providedEmail, setProvidedEmail] = useState<string>();
    const registerMutation = useRegisterMutation();

    const { data: inviteCodeResponse, isFetching } = useQuery({
        queryKey: ["inviteCode", inviteCode],
        queryFn: () => validateInviteCode(inviteCode!),
        refetchOnWindowFocus: false,
        enabled: !!inviteCode,
    });

    const { go, currentStep, nextStep, isExiting } =
        useManageSteps<RegistrationSteps>(true, () => "start");

    const requestVerificationCode = useCallback(async (email: string) => {
        try {
            await login(email);
        } catch {
            return;
        }
    }, []);

    const handleRegistrationStarted = useCallback(
        async (email: string) => {
            setProvidedEmail(email);
            await registerMutation.mutateAsync({
                email,
                inviteCode,
                signupPurpose,
            });
            await requestVerificationCode(email);

            go("verify");
        },
        [
            registerMutation,
            inviteCode,
            signupPurpose,
            requestVerificationCode,
            go,
        ],
    );

    const verify: Required<CodeVerificationProps>["verify"] = useCallback(
        async (verificationCode) =>
            providedEmail
                ? await finishPasswordlessLogin(providedEmail, verificationCode)
                : false,
        [providedEmail],
    );

    let main: React.ReactNode;

    switch (currentStep) {
        case "start":
            main = (
                <Register
                    onSubmit={handleRegistrationStarted}
                    initialEmail={providedEmail ?? inviteCodeResponse?.email}
                />
            );
            break;
        case "verify":
            main = (
                <RegisterCodeVerification
                    email={providedEmail!}
                    verifyCode={verify}
                    requestVerification={() =>
                        requestVerificationCode(providedEmail!)
                    }
                    onBack={() => {
                        setProvidedEmail(undefined);
                        go("start");
                    }}
                />
            );
            break;
    }

    if (isFetching) {
        return <Loader />;
    } else if (inviteCodeResponse && !inviteCodeResponse.valid) {
        main = <p>This invite link has already been used.</p>;
    }

    return (
        <OnboardingLayout
            isExiting={isExiting}
            currentStep={currentStep}
            nextStep={nextStep}
            main={main}
        />
    );
};
